import React, { useEffect, useState } from "react";
import {
  AlertCircle,
  ArrowDownToLine,
  ArrowUpFromLine,
  Loader2,
  Minus,
  Plus,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

const IN = "in";
const OUT = "out";

/**
 * Movimentação rápida do estoque físico de um lote: entrada (reposição,
 * devolução) ou saída (perda, consumo interno). Reservas de pedidos não
 * podem sair: a saída fica limitada ao disponível.
 *
 * Props: `open`, `onOpenChange`, `stock` (lote), `onSave(delta)`, `loading`.
 */
export default function StockMovementDialog({
  open,
  onOpenChange,
  stock,
  onSave,
  loading = false,
}) {
  const [type, setType] = useState(IN);
  const [quantity, setQuantity] = useState("1");
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setType(IN);
      setQuantity("1");
      setError("");
    }
  }, [open, stock]);

  const physical = Number(stock?.current_quantity || 0);
  const reserved = Number(stock?.reserved_quantity || 0);
  const available = Math.max(physical - reserved, 0);
  const amount = Number(quantity);
  const validAmount = Number.isInteger(amount) && amount > 0;
  const delta = validAmount ? (type === IN ? amount : -amount) : 0;
  const nextPhysical = physical + delta;
  const nextAvailable = nextPhysical - reserved;

  const changeQuantity = (value) => {
    setQuantity(value);
    setError("");
  };

  const step = (by) => {
    const base = validAmount ? amount : 0;
    changeQuantity(String(Math.max(base + by, 1)));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validAmount) {
      setError("Informe uma quantidade inteira maior que zero");
      return;
    }
    if (type === OUT && amount > available) {
      setError(
        available > 0
          ? `Só há ${available} un. disponíveis para saída${
              reserved > 0 ? ` (${reserved} reservadas para pedidos)` : ""
            }`
          : "Não há unidades disponíveis para saída neste lote"
      );
      return;
    }
    onSave(delta);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !loading && onOpenChange(next)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">Movimentar estoque</DialogTitle>
          <DialogDescription>
            {stock?.product_name} · Lote {stock?.lot_code}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Tipo de movimentação */}
          <div
            className="grid grid-cols-2 gap-1 rounded-full bg-surface p-1"
            role="radiogroup"
            aria-label="Tipo de movimentação"
          >
            {[
              { value: IN, label: "Entrada", Icon: ArrowDownToLine },
              { value: OUT, label: "Saída", Icon: ArrowUpFromLine },
            ].map(({ value, label, Icon }) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={type === value}
                onClick={() => {
                  setType(value);
                  setError("");
                }}
                className={cn(
                  "flex h-9 items-center justify-center gap-1.5 rounded-full text-sm font-semibold transition-colors",
                  type === value
                    ? value === IN
                      ? "bg-primary text-primary-foreground shadow-sm"
                      : "bg-terra text-white shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </div>

          {/* Quantidade */}
          <div className="space-y-2">
            <Label htmlFor="movement_quantity">Quantidade (un.)</Label>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => step(-1)}
                aria-label="Diminuir quantidade"
                className="flex h-10 w-10 items-center justify-center rounded-full bg-surface text-primary transition-colors hover:bg-muted"
              >
                <Minus className="h-4 w-4" />
              </button>
              <input
                id="movement_quantity"
                type="number"
                inputMode="numeric"
                min="1"
                step="1"
                value={quantity}
                onChange={(e) => changeQuantity(e.target.value)}
                aria-invalid={!!error}
                className={cn(
                  "h-10 w-full rounded-lg border bg-card px-3 text-center font-display text-lg font-bold text-primary focus:border-primary focus:outline-none focus:ring-[3px] focus:ring-brand-pistachio/25",
                  error ? "border-destructive" : "border-border"
                )}
              />
              <button
                type="button"
                onClick={() => step(1)}
                aria-label="Aumentar quantidade"
                className="flex h-10 w-10 items-center justify-center rounded-full bg-surface text-primary transition-colors hover:bg-muted"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
            {error && (
              <p className="flex items-center gap-1 text-sm text-destructive">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {error}
              </p>
            )}
          </div>

          {/* Antes → depois */}
          <div className="grid grid-cols-2 gap-2 rounded-lg bg-surface p-3 text-sm">
            <div>
              <p className="text-[11px] font-semibold text-muted-foreground">
                Estoque físico
              </p>
              <p className="font-display font-bold text-foreground">
                {physical} →{" "}
                <span className={cn(nextPhysical < 0 && "text-destructive")}>
                  {nextPhysical}
                </span>{" "}
                un.
              </p>
            </div>
            <div>
              <p className="text-[11px] font-semibold text-muted-foreground">
                Disponível
              </p>
              <p className="font-display font-bold text-foreground">
                {available} →{" "}
                <span
                  className={cn(
                    nextAvailable < 0 ? "text-destructive" : "text-primary"
                  )}
                >
                  {nextAvailable}
                </span>{" "}
                un.
              </p>
            </div>
            {reserved > 0 && (
              <p className="col-span-2 text-[11px] text-muted-foreground">
                {reserved} un. reservadas para pedidos em andamento não podem
                sair.
              </p>
            )}
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Salvando...
                </>
              ) : type === IN ? (
                "Registrar entrada"
              ) : (
                "Registrar saída"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
